import { Box, Stack, Typography, useTheme } from "@mui/material";
import { SenecFPPTheme, SenecFunctionalButton } from "src/newsenec-ui-components";
import { useState } from "react";
import { SenecSignatureDialog } from "src/components/inputs/senec-signature/SenecSignatureDialog";

export interface SenecSignatureInputProps {
    /**
     * Caption for the button that opens the signature dialog.
     */
    openDialogCaption: string;
    /**
     * Placeholder or label for the signature holder.
     */
    signatureHolder: string;
    /**
     * Title of the signature dialog.
     */
    dialogTitle: string;
    /**
     * Text for the clear button in the dialog.
     */
    clearButtonText: string;
    /**
     * Text for the save button in the dialog.
     */
    saveButtonText: string;
    /**
     * (optional) Callback function triggered when the signature changes.
     * @param dataUrl The updated signature as a data URL.
     */
    onChange?: (signatureSvg?: string) => void;
    /**
     * Test ID for the component, useful for testing purposes.
     */
    dataTestId: string;
    /**
     * (optional) Maximum height for the signature
     */
    signatureMaxHeight?: number;
    /**
     * (optional) Maximum width for the signature
     */
    signatureMaxWidth?: number;
    /**
     * (optional) Sets input to error state.
     */
    error?: boolean;
    /**
     * (optional) Sets displyaed helper text.
     */
    helperText?: string;
}

/**
 * `SenecSignatureInput` is a component that allows users to create, view, and edit a signature.
 * It provides a button to open a dialog where the user can draw their signature.
 * The signature can be saved, cleared, or canceled.
 */
export const SenecSignatureInput = (props: SenecSignatureInputProps) => {
    const [signatureSvg, setSignatureSvg] = useState<string>();

    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const handleOpen = () => setIsDialogOpen(true);
    const handleClose = () => setIsDialogOpen(false);

    const saveSignature = (signatureSvg: string) => {
        props.onChange?.(signatureSvg);
        setSignatureSvg(signatureSvg);
        handleClose();
    };

    const theme: SenecFPPTheme = useTheme();

    return (
        <>
            <Stack>
                <Box
                    position="relative"
                    border="1px solid"
                    borderColor={props.error ? theme.palette.error.main : theme.palette.grey[300]}
                    borderRadius={theme.shape.borderRadius}
                    padding={theme.spacing(1)}
                    display="flex"
                    minHeight="200px"
                    justifyContent="center"
                    alignItems="center"
                    onClick={handleOpen}
                    data-testid={"signature-container"}
                >
                    {signatureSvg ? (
                        <Box component="img" src={signatureSvg} data-testid={"signature-image"} />
                    ) : (
                        <SenecFunctionalButton level="tertiary" onClick={handleOpen} data-testid={"add-signature-btn"}>
                            {props.openDialogCaption}
                        </SenecFunctionalButton>
                    )}

                    <Typography position="absolute" bottom="8px" right="12px">
                        {props.signatureHolder}
                    </Typography>
                </Box>
                {props.helperText && (
                    <Typography
                        color={props.error ? theme.palette.error.main : theme.palette.text.secondary}
                        marginTop="4px"
                        fontSize={"14px"}
                        data-testid={"signature-helper-text"}
                    >
                        {props.helperText}
                    </Typography>
                )}
            </Stack>
            <SenecSignatureDialog
                isOpen={isDialogOpen}
                onReturn={handleClose}
                onClose={handleClose}
                title={props.dialogTitle}
                clearButtonText={props.clearButtonText}
                saveButtonText={props.saveButtonText}
                onSave={saveSignature}
                dataTestId={props.dataTestId}
                signatureMaxHeight={props.signatureMaxHeight}
                signatureMaxWidth={props.signatureMaxWidth}
            />
        </>
    );
};
